<template>
  <div :class="['select-none flex w-[160px] whitespace-nowrap shadow-lg p-2 rounded-full items-center justify-center', toggle_color]">
    <ChevronLeft
      v-if="!editing_disabled"
      v-tooltip="`Toggle status left`"
      :disabled="!can_toggle_left"
      :class="[
        ` text-white dark:text-black h-5 w-5 disabled:hover:cursor-default !bg-transparent cursor-pointer disabled:hover:text-white hover:shadow-xl shadow-inner opacity-0`,
        { disabled: !can_toggle_left, 'opacity-100': can_toggle_left, 'rotate-180': $store.state.portfolio.ui_options?.language == 'ar' },
        toggle_color
      ]"
      @click.stop="() => (can_toggle_left ? toggleStatus('left') : void null)" />

    <Menu as="div" class="relative flex-1 flex-shrink-0 text-center cursor-default">
      <MenuButton
        :class="[
          `select-none  text-white dark:text-black disabled:hover:cursor-default ${'cursor-pointer'} disabled:hover:text-white hover:shadow-xl shadow-inner inline-flex w-[90px]  items-center justify-center border-1 rounded-full focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue`
        ]"
        @click.stop>
        <span class="capitalize">{{ statuses.find((s) => s.value == real_status)?.label || real_status }}</span>
      </MenuButton>
      <transition
        v-if="!disableToggle && !editing_disabled"
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0">
        <MenuItems
          v-if="!hideDropDown"
          class="absolute z-10 mx-3 -ml-8 origin-bottom-right bg-transparent border-0 w-[165px] space-y-1 mt-2.5 focus:outline-none text-center">
          <div v-for="status in object_statuses.filter((s) => s !== real_status)" :key="status" class="">
            <div class="">
              <MenuItem v-slot="{ active }">
                <div
                  :class="[
                    active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                    'border bg-white dark:bg-dark-screen-background rounded-full dark:border-dark-divider-border cursor-pointer group flex items-center px-4 py-2 text-sm w-full justify-center capitalize shadow-lg'
                  ]"
                  @click.stop="switchStatus(status)">
                  {{ status }}
                </div>
              </MenuItem>
            </div>
          </div>
          <div v-if="object_type == 'plan'" class="">
            <div class="">
              <MenuItem v-slot="{ active }">
                <div
                  :class="[
                    active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                    'border bg-white dark:bg-dark-screen-background rounded-full dark:border-dark-divider-border cursor-pointer group flex items-center px-4 py-2 text-sm w-full justify-center capitalize shadow-lg'
                  ]"
                  @click.stop="enableOrDisableDynamicStatus">
                  {{ show_dynamic_status ? $t("Manual Status") : $t("Automatic Status") }}
                </div>
              </MenuItem>
            </div>
          </div>
        </MenuItems>
      </transition>
    </Menu>
    <ChevronRight
      v-if="!editing_disabled"
      v-tooltip="`Toggle status right`"
      :disabled="!can_toggle_right"
      :class="[
        `text-white dark:text-black h-5 w-5 disabled:hover:cursor-default !bg-transparent dark:bg-dark-screen-background cursor-pointer disabled:hover:text-white hover:shadow-xl shadow-inner opacity-0`,
        { disabled: !can_toggle_right, 'opacity-100': can_toggle_right, 'rotate-180': $store.state.portfolio.ui_options?.language == 'ar' },
        toggle_color
      ]"
      tooltip="Toggle status right"
      @click.stop="() => (can_toggle_right ? toggleStatus('right') : void null)" />
  </div>
</template>

<script>
import { ChevronRight, ChevronLeft } from "lucide-vue-next"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    ChevronRight,
    ChevronLeft
  },
  inject: ["canUserEditLayout"],
  props: {
    preventRequest: null,
    object: {
      type: Object,
      required: true
    },
    hideDropDown: {
      type: Boolean,
      required: false
    },
    disableToggle: {
      type: Boolean,
      required: false
    }
  },
  emits: ["statusChanged", "dynamicStatusChanged"],
  data() {
    return {
      current_status: "pending",
      show_dynamic_status: false,
      update_timeout: null,
      statuses: [
        { label: this.$t("Not started"), value: "pending" },
        { label: this.$t("In progress"), value: "in-progress" },
        { label: this.$t("Overdue"), value: "overdue" },
        { label: this.$t("Completed"), value: "completed" }
      ]
    }
  },
  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    },
    can_toggle_left() {
      return !this.disableToggle && this.object_statuses.findIndex((s) => s == this.current_status) > 0
    },
    can_toggle_right() {
      return !this.disableToggle && this.object_statuses.findIndex((s) => s == this.current_status) < this.object_statuses.length - 1
    },

    object_type() {
      return this.object.otype || this.object.o_type
    },

    dynamic_status() {
      if (this.object_type == "plan" && this.object.summary?.dynamic_status) {
        return this.object.summary?.dynamic_status
      }
      return this.object.status
    },

    real_status() {
      if (this.show_dynamic_status) {
        return this.dynamic_status
      }
      return this.current_status
    },
    toggle_color() {
      return {
        not_started: "dark:bg-dark-light-blue bg-light-light-blue",
        pending: "dark:bg-dark-light-blue bg-light-light-blue",
        "in-progress": "dark:bg-dark-brand-blue bg-light-brand-blue",
        overdue: "bg-status-warning-yellow dark:bg-status-warning-yellow",
        completed: "bg-status-success-green dark:bg-status-success-green",
        cancelled: "bg-status-error-red dark:bg-status-error-red",
        missed: "bg-status-error-red dark:bg-status-error-red"
      }[this.real_status]
    },
    object_statuses() {
      if (this.object_type == "task") {
        if (this.current_status == "overdue") {
          return ["overdue", "completed", "cancelled"]
        } else {
          return ["pending", "in-progress", "completed", "cancelled"]
        }
      } else {
        //plan,goal
        if (this.show_dynamic_status) {
          return []
        } else {
          if (this.current_status == "overdue") {
            return ["overdue", "completed", "missed"]
          } else {
            return ["pending", "in-progress", "completed", "missed"]
          }
        }
      }
    }
  },
  watch: {
    //when object is updated in form status changes in table
    object() {
      this.current_status = this.object.status
      this.show_dynamic_status = this.object.show_dynamic_status
      if (this.object.due_date) {
        if (
          (this.current_status == "pending" || this.current_status == "in-progress") &&
          this.$moment(this.object.due_date).local().startOf("day").isBefore(this.$moment().startOf("day"))
        ) {
          this.current_status = "overdue"
        }
      }
    }
  },
  created() {
    this.show_dynamic_status = this.object.show_dynamic_status
    this.current_status = this.object.status
    if (this.object.due_date) {
      if (
        (this.current_status == "pending" || this.current_status == "in-progress") &&
        this.$moment(this.object.due_date).local().startOf("day").isBefore(this.$moment().startOf("day"))
      ) {
        this.current_status = "overdue"
      }
    }
  },
  methods: {
    enableOrDisableDynamicStatus() {
      this.show_dynamic_status = !this.show_dynamic_status
      const data = {
        plan: {
          id: this.object.id,
          show_dynamic_status: this.show_dynamic_status,
          dont_load: true
        }
      }
      this.$emit("dynamicStatusChanged", this.show_dynamic_status)
      if (!this.preventRequest) {
        this.$store.dispatch("data/updatePlan", data).then(() => {})
      }
    },
    toggleStatus(direction) {
      let status_index = this.object_statuses.findIndex((s) => s == this.current_status)
      if (direction == "left") {
        status_index -= 1
      } else {
        status_index += 1
      }
      this.switchStatus(this.object_statuses[status_index])
    },
    switchStatus(status) {
      this.current_status = status
      const data = {}
      data[this.object_type] = {
        id: this.object.id,
        record_full: this.object.record,
        status: status,
        dont_load: true
      }
      let store_action_name = utils.capitalize(this.object_type)
      clearTimeout(this.update_timeout)

      if (this.preventRequest) {
        this.$emit("statusChanged", status)
      } else {
        this.update_timeout = setTimeout(() => {
          if (store_action_name) {
            this.$store
              .dispatch("data/update" + store_action_name, data)
              .then((res) => {
                this.$emitter.emit(ALL_EMITTERS[`${store_action_name.toUpperCase()}_UPDATED_IN_AI_PROMPT`], res)

                this.$emit("statusChanged", status)
              })
              .catch(() => {
                this.$emit("statusChanged", status)
              })
          } else {
            this.$emit("statusChanged", status)
          }
        }, 200)
      }
    }
  }
}
</script>

<style>
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
